import { useState, useEffect } from "react";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import ProductTable from "../../components/tables/Products/ProductTable";
import Button from "../../components/ui/button/Button";
import Select from "../../components/form/Select";
import { BoxIconLine } from "../../icons";
import { Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import CompanyCoproducer from "../Co-Producer/CompanyCoproducer";
import CompanyAffiliates from "../Affiliates/CompanyAffiliates"
import CompanyTerms from "../Terms/CompanyTerms"

type TabType = "meus-produtos" | "co-producoes" | "afiliados" | "termos";

const getTabFromQueryParam = (param: string | null): TabType => {
    switch (param) {
        case "mycoproductions":
            return "co-producoes";
        case "myaffiliates":
            return "afiliados";
        case "terms":
            return "termos";
        default:
            return "meus-produtos";
    }
};

const getQueryParamFromTab = (tab: TabType): string | null => {
    switch (tab) {
        case "co-producoes": return "mycoproductions";
        case "afiliados": return "myaffiliates";
        case "termos": return "terms";
        default: return null;
    }
};

export default function MyProducts() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Estado para controlar a aba ativa (inicializa com base na URL)
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        const tabParam = searchParams.get("tab");
        return getTabFromQueryParam(tabParam);
    });

    // Sincronizar URL com a aba ativa
    useEffect(() => {
        const currentTab = searchParams.get("tab");
        const expectedParam = getQueryParamFromTab(activeTab);

        if (currentTab !== expectedParam) {
            if (expectedParam) {
                setSearchParams({ tab: expectedParam });
            } else {
                setSearchParams({});
            }
        }
    }, [activeTab, searchParams, setSearchParams]);

    // Estados separados para pesquisa de cada aba
    const [searchTermProdutos, setSearchTermProdutos] = useState("");
    const [searchTermTermos, setSearchTermTermos] = useState("");

    // Estados separados para filtros de cada aba
    const [statusFilterProdutos, setStatusFilterProdutos] = useState("");

    // Opções para o select
    const options = [
        { value: "all", label: "Todos os status" },
        { value: "active", label: "Ativo" },
        { value: "draft", label: "Em rascunho" },
        { value: "pending", label: "Pendente" },
        { value: "inactive", label: "Inativo" }
    ];

    // Função para mudar de aba com tipagem correta
    const handleTabChange = (tabId: TabType) => {
        setActiveTab(tabId);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Meus Produtos"
                description="Gerenciar produtos"
            />
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12 mb-6">
                    <div className="flex justify-between items-center">
                        <div className="w-auto">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                                Produtos
                            </h3>
                        </div>
                        <div className="w-auto">
                            <Button size="sm" variant="primary" startIcon={<BoxIconLine className="size-5" />} onClick={() => navigate("/products/new")}>
                                Novo Produto
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sistema de abas */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-800">
                <ul className="flex flex-wrap -mb-px">
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("meus-produtos")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "meus-produtos"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Meus produtos
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("co-producoes")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "co-producoes"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Minhas Co-produções
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("afiliados")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "afiliados"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Sou Afiliado
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("termos")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "termos"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Termos de Uso
                        </button>
                    </li>
                </ul>
            </div>

            {/* Filtros acima da tabela - aba "Termos de Uso" */}
            {activeTab === "termos" && (
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar..."
                                value={searchTermTermos}
                                onChange={(e) => setSearchTermTermos(e.target.value)}
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>
                        <Button size="sm" variant="primary" startIcon={<Plus className="size-5" />} onClick={() => navigate("/products/terms/new")}>
                            Novo Termo
                        </Button>
                    </div>
                </div>
            )}

            {/* Filtros acima da tabela - apenas para aba "Meus produtos" */}
            {activeTab === "meus-produtos" && (
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Campo de busca */}
                        <div className="relative flex-1">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar..."
                                value={searchTermProdutos}
                                onChange={(e) => setSearchTermProdutos(e.target.value)}
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        {/* Select */}
                        <div className="w-full md:w-64">
                            <Select
                                options={options}
                                placeholder="Filtrar por status"
                                onChange={(value) => setStatusFilterProdutos(value)}
                                className="dark:bg-dark-900 h-11"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Conteúdo das abas */}
            <div className="space-y-6">
                {activeTab === "meus-produtos" && (
                    <ComponentCard title="Meus Produtos">
                        <ProductTable
                            searchTerm={searchTermProdutos}
                            statusFilter={statusFilterProdutos}
                            type="owner"
                        />
                    </ComponentCard>
                )}

                {activeTab === "co-producoes" && (
                    <CompanyCoproducer />
                )}

                {activeTab === "afiliados" && (
                    <CompanyAffiliates />
                )}

                {activeTab === "termos" && (
                    <CompanyTerms searchTerm={searchTermTermos} />
                )}
            </div>
        </>
    );
}